/**
 * One textarea into the item list `POST /api/orders` expects.
 *
 * This replaces `ItemRows` — four inputs per item, repeated. The trade is the whole
 * point of the redesign: four boxes per item is four places to get lost, and the
 * customer this form is for writes *"3 chicken"* on one line because that is how they
 * have ordered on Messenger for years. The form should read what they already write
 * rather than teach them a new shape.
 *
 * **Everything here is forgiving on the way in and strict on the way out.** A typo must
 * not become a rejected submit, because the API returns one opaque message with no
 * field-level detail — there is nothing to point at. So a zero is read as one, a minus
 * sign stays in the name, and an over-long line is trimmed rather than refused. What
 * comes out always satisfies the API, whatever went in.
 */

/** The API's own bounds, mirrored so a parse can never produce a body it will refuse. */
export const ITEM_LIMITS = {
  maxItems: 50,
  maxNameLength: 120,
  maxQuantity: 999,
}

/**
 * A leading count, in the two forms people actually type.
 *
 * `3 chicken` and `3x chicken`. The separator is required — it is what stops `3` on its
 * own from being a quantity with nothing to count, and what keeps `3 xl shirt` from
 * becoming three of *"l shirt"*: the `\s` alternative matches the space, so the `x`
 * stays where it belongs, at the front of the size.
 *
 * `-1 chicken` matches nothing here, which is deliberate and is the whole of the
 * negative-quantity guard: the minus is simply part of the name.
 */
const LEADING_COUNT = /^(\d+)(?:[xX]|\s)\s*(.+)$/

const clamp = (value, low, high) => Math.min(high, Math.max(low, value))

const parseLine = (line) => {
  const match = LEADING_COUNT.exec(line)

  if (!match) {
    return { name: line.slice(0, ITEM_LIMITS.maxNameLength), quantity: 1 }
  }

  const [, count, rest] = match

  return {
    name: rest.trim().slice(0, ITEM_LIMITS.maxNameLength),
    // A zero is a typo, and the thing was still asked for — so the floor is 1, not 0.
    quantity: clamp(Number(count), 1, ITEM_LIMITS.maxQuantity),
  }
}

/**
 * @param {string} text — the raw textarea value
 * @returns {{name: string, quantity: number}[]} — every item, already inside the API's
 *   bounds. Never contains a blank name and never a quantity outside 1–999.
 */
export function parseItems(text) {
  if (typeof text !== 'string') {
    return []
  }

  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    // Sliced before parsing rather than after: the cap keeps what the customer wrote
    // first, and there is no reason to parse forty lines that will be dropped.
    .slice(0, ITEM_LIMITS.maxItems)
    .map(parseLine)
    // A line of only whitespace after a count — `"3   "` — parses to an empty name.
    // Nothing was named, so nothing is ordered.
    .filter((item) => item.name.length > 0)
}
